import { EventType, InteractionType } from "@azure/msal-browser";
import { useMsal, useMsalAuthentication } from "@azure/msal-react";
import {
  EventSourceMessage,
  fetchEventSource,
} from "@microsoft/fetch-event-source";
import { useCallback, useEffect, useRef, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { loginRequest } from "../config/authConfig";
import { keysToSnakeCase } from "../utils/caseConversion";
import {
  clearLegalActiveSseTaskId,
  clearPendingLegalTaskSession,
} from "../utils/legalSearchTaskFlow";

interface UseSseWithMsalProps {
  handleMessage: (currentData: any[], event: EventSourceMessage) => any[];
}

/**
 * Erreur « fatale » pour fetch-event-source : à relancer depuis onopen/onerror
 * pour arrêter les reconnexions (voir README @microsoft/fetch-event-source).
 */
class ExhaustiveSearchResumeNoJobError extends Error {
  constructor() {
    super("No active exhaustive search job for this thread");
    this.name = "ExhaustiveSearchResumeNoJobError";
  }
}

function isExhaustiveSearchResumeEndpoint(endpoint: string): boolean {
  return endpoint.includes("/chat/exhaustive-search/resume");
}

/**
 * Custom hook for handling Server-Sent Events (SSE) with MSAL authentication.
 * Provides methods to execute requests and manage SSE connections.
 */
const useSseWithMsal = ({ handleMessage }: UseSseWithMsalProps) => {
  const { instance } = useMsal();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [activeThreadIds, setActiveThreadIds] = useState<string[]>([]);
  const [error, setError] = useState<any | null>(null);
  const [data, setData] = useState<any[]>([]);

  // Plusieurs streams SSE peuvent coexister (ne pas couper les anciens en lançant un nouveau).
  const controllers = useRef<Map<string, AbortController>>(new Map());
  const requestThreadIds = useRef<Map<string, string | undefined>>(new Map());

  const syncActiveThreadIds = useCallback(() => {
    const ids = Array.from(requestThreadIds.current.values()).filter(
      (v): v is string => Boolean(v)
    );
    setActiveThreadIds(Array.from(new Set(ids)));
  }, []);

  const activeAccount = instance.getActiveAccount();
  const { result, error: msalError } = useMsalAuthentication(
    InteractionType.Popup,
    {
      ...loginRequest,
      account: activeAccount || undefined,
    }
  );

  /**
   * Constructs headers for the SSE request.
   * @param requestData - Optional request data
   * @returns Request headers
   */
  const constructHeaders = (requestData: object | null) => {
    const headers: { [key: string]: string } = {
      Authorization: `Bearer ${result?.accessToken || ""}`,
      "Access-Control-Expose-Headers": "",
      Accept: "text/event-stream",
    };
    if (requestData) headers["Content-Type"] = "application/json";
    return headers;
  };

  /**
   * Handles errors during SSE fetching.
   * @param err - Error object
   */
  const handleFetchError = (err: any) => {
    console.error("Error from server", err);
    setError(err);
    setIsLoading(false);
    clearLegalActiveSseTaskId();
    clearPendingLegalTaskSession();
    throw err;
  };

  /**
   * Executes an SSE request with authentication and handles events.
   * @param method - HTTP method (e.g., GET)
   * @param endpoint - API endpoint URL
   * @param requestData - Optional request data
   */
  const execute = useCallback(
    async (
      method: string,
      endpoint: string,
      requestData: object | null = null
    ) => {
      if (msalError) {
        console.error("MSAL Error:", msalError);
        setError(msalError);
        return;
      }

      if (!result) {
        console.error("No authentication result");
        return;
      }

      if (result) {
        const requestId = uuidv4();
        const threadIdFromPayload = requestData
          ? String(
              (requestData as any).threadId ??
                (requestData as any).thread_id ??
                ""
            )
          : "";
        try {
          const controller = new AbortController();
          controllers.current.set(requestId, controller);
          requestThreadIds.current.set(
            requestId,
            threadIdFromPayload || undefined
          );
          syncActiveThreadIds();
          setIsLoading(true);
          const { signal } = controller;

          const headers = constructHeaders(requestData);
          if (requestData) {
            requestData = keysToSnakeCase(requestData);
            (requestData as any)["user_id"] =
              instance.getActiveAccount()?.username || null;
          }

          await fetchEventSource(endpoint, {
            openWhenHidden: true,
            method,
            headers,
            body: requestData ? JSON.stringify(requestData) : null,
            signal,
            async onopen(res) {
              if (
                isExhaustiveSearchResumeEndpoint(endpoint) &&
                res.status === 404
              ) {
                controllers.current.delete(requestId);
                requestThreadIds.current.delete(requestId);
                syncActiveThreadIds();
                setIsLoading(controllers.current.size > 0);
                clearLegalActiveSseTaskId();
                clearPendingLegalTaskSession();
                throw new ExhaustiveSearchResumeNoJobError();
              }
              if (res.ok && res.status === 200) {
                console.log("Connection established", res);
              } else if (
                res.status >= 400 &&
                res.status < 500 &&
                res.status !== 429
              ) {
                controllers.current.delete(requestId);
                requestThreadIds.current.delete(requestId);
                syncActiveThreadIds();
                setIsLoading(controllers.current.size > 0);
                clearLegalActiveSseTaskId();
                clearPendingLegalTaskSession();
                console.log("Client-side error", res);
              }
            },
            onmessage: (event) => {
              try {
                if (threadIdFromPayload) {
                  try {
                    const parsed = JSON.parse(event.data);
                    if (
                      parsed?.object_type === "Message" &&
                      parsed?.data &&
                      !parsed.data.thread_id
                    ) {
                      parsed.data.thread_id = threadIdFromPayload;
                      event = {
                        ...event,
                        data: JSON.stringify(parsed),
                      };
                    }
                  } catch {
                    // ignore invalid JSON payload in pre-processing
                  }
                }
                setData((currentMessages) =>
                  handleMessage(currentMessages, event)
                );
              } catch (e) {
                console.error("Error processing message", e);
              }
            },
            onclose() {
              console.log("Connection closed by the server");
              controllers.current.delete(requestId);
              requestThreadIds.current.delete(requestId);
              syncActiveThreadIds();
              setIsLoading(controllers.current.size > 0);
              clearLegalActiveSseTaskId();
              /* pending conservé jusqu’à emitLegalTaskProgressCompleted (ChatInput) ou reload reconcile */
            },
            onerror(err) {
              if (err.name === "AbortError") {
                console.log("Fetch aborted");
                controllers.current.delete(requestId);
                requestThreadIds.current.delete(requestId);
                syncActiveThreadIds();
                setIsLoading(controllers.current.size > 0);
                clearLegalActiveSseTaskId();
                clearPendingLegalTaskSession();
              } else if (err instanceof ExhaustiveSearchResumeNoJobError) {
                controllers.current.delete(requestId);
                requestThreadIds.current.delete(requestId);
                syncActiveThreadIds();
                setIsLoading(controllers.current.size > 0);
                clearLegalActiveSseTaskId();
                clearPendingLegalTaskSession();
                throw err;
              } else {
                handleFetchError(err);
              }
            },
          });
        } catch (e) {
          if (e instanceof ExhaustiveSearchResumeNoJobError) {
            controllers.current.delete(requestId);
            requestThreadIds.current.delete(requestId);
            syncActiveThreadIds();
            setIsLoading(controllers.current.size > 0);
            clearLegalActiveSseTaskId();
            clearPendingLegalTaskSession();
            // Propager pour que l’appelant (ex. resumeExhaustiveSearchStream) puisse
            // rafraîchir l’historique du fil — le job est fini côté serveur mais les
            // messages (synthèse finale) peuvent déjà être en base.
            throw e;
          }
          if (isExhaustiveSearchResumeEndpoint(endpoint)) {
            controllers.current.delete(requestId);
            requestThreadIds.current.delete(requestId);
            syncActiveThreadIds();
            setIsLoading(controllers.current.size > 0);
            clearLegalActiveSseTaskId();
            clearPendingLegalTaskSession();
            if ((e as Error)?.name === "AbortError") {
              return;
            }
          }
          handleFetchError(e);
        }
      }
    },
    [result, msalError, activeAccount, handleMessage, syncActiveThreadIds]
  );

  /**
   * Aborts the ongoing SSE request.
   */
  const abort = useCallback(() => {
    controllers.current.forEach((ctrl) => ctrl.abort());
    controllers.current.clear();
    requestThreadIds.current.clear();
    setActiveThreadIds([]);
    setIsLoading(false);
    /** Sans ça, useChat continue à fusionner l’ancien buffer SSE et le chat « avance » encore. */
    setData([]);
    clearLegalActiveSseTaskId();
    clearPendingLegalTaskSession();
  }, []);

  /** Déconnexion / retrait du compte : arrêter le SSE tout de suite (la barre de nav n’a pas accès à abortQuery). */
  useEffect(() => {
    const callbackId = instance.addEventCallback((event) => {
      if (
        event.eventType === EventType.LOGOUT_START ||
        event.eventType === EventType.ACCOUNT_REMOVED
      ) {
        abort();
      }
    });
    return () => {
      if (callbackId) {
        instance.removeEventCallback(callbackId);
      }
    };
  }, [instance, abort]);

  /** Filet de sécurité si le compte disparaît sans événement (ex. autre onglet). */
  useEffect(() => {
    if (!activeAccount && controllers.current.size > 0) {
      abort();
    }
  }, [activeAccount, abort]);

  useEffect(() => {
    // Cleanup abort controller on component unmount
    return () => {
      abort();
    };
  }, [abort]);

  return {
    isLoading,
    activeThreadIds,
    error,
    data,
    execute,
    abort,
  };
};

export default useSseWithMsal;
